import { matchPath } from "react-router-dom";

// Public marketing pages exist once per language: Arabic at the root
// (`/our-services`) and English under `/en` (`/en/our-services`). On these
// pages the URL decides the language (LanguageProvider), so each language is
// its own crawlable URL with hreflang alternates (usePageMeta). Every other
// route — dashboard, portals, invoices, auth — is language-less and keeps the
// visitor's stored preference.

export type UrlLanguage = "ar" | "en";

export const EN_PREFIX = "/en";

/** Root (Arabic) path patterns of the pages that also exist under /en. */
export const MARKETING_PATHS = [
  "/",
  "/about-us",
  "/our-services",
  "/self-management",
  "/advertisements",
  "/advertisements/:id",
  "/advertisements/property/:id",
  "/projects/:id",
  "/p/:id",
  "/blog",
  "/blog/:slug",
  "/privacy-policy",
  "/terms",
] as const;

const trimSlash = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : p);

/** True when `rootPath` (no /en prefix) is a marketing page. */
export function isMarketingPath(rootPath: string): boolean {
  const p = trimSlash(rootPath);
  return MARKETING_PATHS.some((pattern) => matchPath({ path: pattern, end: true }, p) !== null);
}

/** The language a URL imposes: "en" under /en, "ar" for root marketing pages, else null. */
export function urlLanguageOf(pathname: string): UrlLanguage | null {
  const p = trimSlash(pathname);
  if (p === EN_PREFIX || p.startsWith(`${EN_PREFIX}/`)) {
    return isMarketingPath(p.slice(EN_PREFIX.length) || "/") ? "en" : null;
  }
  return isMarketingPath(p) ? "ar" : null;
}

/** The root (Arabic) form of a marketing URL: strips a leading /en. */
export function rootPathOf(pathname: string): string {
  const p = trimSlash(pathname);
  if (p === EN_PREFIX) return "/";
  return p.startsWith(`${EN_PREFIX}/`) ? p.slice(EN_PREFIX.length) : p;
}

function safeDecode(path: string): string {
  try {
    return decodeURI(path);
  } catch {
    return path;
  }
}

/**
 * True when two paths name the same page. The router's `location.pathname`
 * stays percent-encoded (`%D8%A7…`) while built paths carry raw Arabic slugs,
 * so both are decoded before comparing.
 */
export function isSamePath(a: string, b: string): boolean {
  return safeDecode(a) === safeDecode(b);
}

/** A marketing page's path in `lang`; other paths are returned unchanged. */
export function localizedPath(path: string, lang: UrlLanguage): string {
  const root = rootPathOf(path);
  if (!isMarketingPath(root)) return path;
  if (lang === "ar") return root;
  return root === "/" ? EN_PREFIX : `${EN_PREFIX}${root}`;
}
